import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { executeWidget, RESULT_STATUS_EMPTY } from '../../../services';
import { toWidgetData } from '../utils/widgetDataAdapter.js';

/**
 * Whether a sample would be a lie.
 *
 * `isEmpty` alone used to answer this, and it left out the execution that
 * **failed**: `data` is then `null` for the same reason it is null before
 * anything has loaded, so the card fell back to its sample and presented
 * fixture numbers as the widget's own. `AI_SUMMARY` showed it plainly — the
 * engine answers `422 DSW028 "Provider execution delegated"` and the card
 * drew `agent-run-1`, "Summary text. One paragraph of the agent's read…",
 * `Finding 1/2/3` (dev, 2026-10-26) — but every type did it on any failed
 * execute.
 *
 * The two stay apart in what they mean: one is "it ran and found nothing",
 * the other "it could not run". Only the sample switch treats them alike.
 *
 * **While it is still loading the sample stays**, which is why `isLoading` is
 * in here: a widget that has not answered yet has not failed, and flashing an
 * empty state on the way to real data is its own kind of wrong.
 */
export const hasNoDataToDraw = ({ active, isLoading, isEmpty, error } = {}) =>
  Boolean(active) && !isLoading && (Boolean(isEmpty) || Boolean(error));

/**
 * The data a saved widget renders (CT-5183).
 *
 * Only a **saved** widget can be executed — the endpoint runs the stored
 * definition — so this stays idle without a uuid rather than asking for data
 * that cannot exist yet. That is also why the builder shows sample data until
 * the first save and real data after it.
 *
 * `data` is `null` in three cases that are not the same answer: nothing has
 * loaded yet, the widget ran and found nothing, and the execution failed.
 * `CatalogWidget` draws a **sample** for the first, which is right — so
 * `hasNoData` tells the other two from it. A caller passes it on as
 * `useSampleData={!hasNoData}`, and anything that ran answers with an empty
 * state instead of invented numbers. `isEmpty` stays for the narrower
 * question of whether the query itself came back empty.
 *
 * `version` is any token that changes when the stored definition does — the
 * widget's `versionStamp`. Passing it is what makes a save re-execute instead
 * of leaving the previous result on screen.
 *
 * `recordUuid` is the open record, for the context types (profile card and
 * the like). It travels at the top level of the execute body and never inside
 * `parameters`, which the engine rejects (`DSW014`).
 *
 * No `type` any more: since the rev4 engine shapes the response for the
 * widget's own type, there is nothing here to map it onto (CT-5641).
 *
 * @param {{ widgetUuid?: string, parameters?: object, recordUuid?: string,
 *           version?: string, enabled?: boolean }} [options]
 */
export const useWidgetData = ({
  widgetUuid,
  parameters,
  recordUuid,
  version,
  enabled = true,
} = {}) => {
  const [state, setState] = useState({ result: null, error: null });
  const [resolvedKey, setResolvedKey] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const fetchIdRef = useRef(0);

  // Serialised so a caller can pass a fresh object every render without
  // re-running the request on each one.
  const parametersKey = useMemo(() => JSON.stringify(parameters ?? null), [parameters]);
  const requestKey = `${widgetUuid ?? ''}|${version ?? ''}|${parametersKey}|${recordUuid ?? ''}|${reloadToken}`;
  const active = Boolean(widgetUuid) && enabled;
  const isLoading = active && resolvedKey !== requestKey;

  useEffect(() => {
    if (!active) return undefined;

    const fetchId = ++fetchIdRef.current;
    let mounted = true;

    executeWidget(widgetUuid, {
      parameters: JSON.parse(parametersKey) ?? undefined,
      recordUuid: recordUuid || undefined,
    })
      .then((result) => {
        if (!mounted || fetchId !== fetchIdRef.current) return;
        setState({ result, error: null });
      })
      .catch((error) => {
        if (!mounted || fetchId !== fetchIdRef.current) return;
        setState({ result: null, error });
      })
      .finally(() => {
        if (mounted && fetchId === fetchIdRef.current) setResolvedKey(requestKey);
      });

    return () => {
      mounted = false;
    };
  }, [active, parametersKey, recordUuid, requestKey, widgetUuid]);

  /*
    Gated on `active`, not just on having a result. Turning the hook off has
    to *drop* what it last read, not merely stop asking: a caller disables it
    because the previous result no longer describes what is on screen, and
    leaving it mapped would keep drawing exactly the stale numbers the caller
    switched off to avoid.

    Nothing else happens here any more. The labels this hook used to patch in
    — the aggregate's, because `execute` echoed the query alias and every
    surface rendered `record_count` — come shaped from the backend now, in
    `data.columns[].label` and each type's own label slots.
  */
  const data = useMemo(
    () => (active ? toWidgetData(state.result) : null),
    [active, state.result],
  );

  /** The widget ran and the query answered with no value — see the note above. */
  const isEmpty = active && state.result?.status === RESULT_STATUS_EMPTY;

  const hasNoData = hasNoDataToDraw({ active, isLoading, isEmpty, error: state.error });

  const retry = useCallback(() => setReloadToken((n) => n + 1), []);

  return useMemo(
    () => ({
      data, isEmpty, hasNoData, result: state.result, isLoading, error: state.error, retry,
    }),
    [data, hasNoData, isEmpty, isLoading, retry, state.error, state.result],
  );
};

export default useWidgetData;
